import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Ticket,
  Trophy,
  Gift,
  Users,
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  Download,
  AlertCircle,
  CheckCircle2,
  Clock,
  Zap,
  User,
  Mail,
  MapPin,
  Instagram,
  File,
  FileText,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase, CampanhaCupom, SorteioCupom, GanhadorSorteio } from '@/lib/supabase';
import { couponsService } from '@/services/coupons.service';
import { httpInterceptor } from '@/services/http.interceptor';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { clientService } from '@/services/client.service';
import { parseDateTimeLocal, formatToDateTimeLocal, formatToISOLocalString } from '@/lib/utils';

// Tipos adicionais
interface ClienteCupons {
  cliente_id: number;
  cliente_nome: string;
  cliente_codigo: string;
  cliente_email: string;
  cliente_telefone: string | null;
  cliente_instagram: string | null;
  cliente_cidade: string | null;
  cliente_estado: string | null;
  total_ativos: number;
  total_inativos: number;
  total_cupons: number;
  cupons: any[];
}

interface FormCampanha extends Partial<CampanhaCupom> {
  nome?: string;
  descricao?: string;
  periodo_inicio?: string;
  periodo_fim?: string;
  status?: 'ativa' | 'pausada' | 'encerrada';
}

interface FormSorteio extends Partial<SorteioCupom> {
  campanha_id?: string;
  titulo?: string;
  data_sorteio?: string;
  tipo?: 'semanal' | 'super_live';
  status?: 'pendente' | 'realizado' | 'cancelado';
}

interface FormGanhador {
  sorteio_id?: string;
  numero_cupom?: string;
  premio_descricao?: string;
  validado?: boolean;
  presente_na_live?: boolean;
  observacoes?: string;
}

interface DadosCupom {
  cupom_id: string;
  numero_cupom_base: string;
  cliente_id: number;
  cliente_nome: string;
  cliente_codigo: number;
  cliente_cidade: string;
  cliente_estado: string;
  cliente_instagram: string | null;
  revendedora_id: number;
  revendedora_nome: string;
  revendedora_codigo: number;
  origem: string;
  venda_id: number | null;
  quantidade_cupons: number;
  data_validade: string;
}

const AdminCupons = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('campanhas');

  // Estados - Campanhas
  const [campanhas, setCampanhas] = useState<CampanhaCupom[]>([]);
  const [campanhaAtiva, setCampanhaAtiva] = useState<CampanhaCupom | null>(null);
  const [loadingCampanha, setLoadingCampanha] = useState(false);
  const [dialogCampanha, setDialogCampanha] = useState(false);
  const [editingCampanha, setEditingCampanha] = useState<CampanhaCupom | null>(null);
  const [formCampanha, setFormCampanha] = useState<FormCampanha>({});
  const [filterAtivas, setFilterAtivas] = useState(false);
  const [selectedBannerFile, setSelectedBannerFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [selectedRegulamentoFile, setSelectedRegulamentoFile] = useState<File | null>(null);
  const [regulamentoFileName, setRegulamentoFileName] = useState<string | null>(null);
  const [uploadingRegulamento, setUploadingRegulamento] = useState(false);

  // Estados - Sorteios
  const [sorteios, setSorteios] = useState<SorteioCupom[]>([]);
  const [loadingSorteios, setLoadingSorteios] = useState(false);
  const [dialogSorteio, setDialogSorteio] = useState(false);
  const [editingSorteio, setEditingSorteio] = useState<SorteioCupom | null>(null);
  const [formSorteio, setFormSorteio] = useState<FormSorteio>({});

  // Estados - Usuários e Cupons
  const [usuarios, setUsuarios] = useState<ClienteCupons[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [searchUsuario, setSearchUsuario] = useState('');
  const [selectedUsuario, setSelectedUsuario] = useState<ClienteCupons | null>(null);
  const [dialogDetalhes, setDialogDetalhes] = useState(false);

  // Estados - Ganhadores
  const [ganhadores, setGanhadores] = useState<GanhadorSorteio[]>([]);
  const [loadingGanhadores, setLoadingGanhadores] = useState(false);
  const [selectedGanhador, setSelectedGanhador] = useState<GanhadorSorteio | null>(null);
  const [dialogGanhador, setDialogGanhador] = useState(false);
  const [dialogNovoGanhador, setDialogNovoGanhador] = useState(false);
  const [formGanhador, setFormGanhador] = useState<FormGanhador>({});
  const [dadosCupom, setDadosCupom] = useState<DadosCupom | null>(null);
  const [loadingBuscaCupom, setLoadingBuscaCupom] = useState(false);
  const [adicionandoGanhador, setAdicionandoGanhador] = useState(false);

  // Estado - Export CSV
  const [loadingExport, setLoadingExport] = useState(false);

  // Confirmação delete
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string } | null>(null);

  useEffect(() => {
    const userDataString = localStorage.getItem('userlogged');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      setUser(userData);

      if (userData.role !== 'admin') {
        toast.error('Acesso restrito a administradores');
        navigate('/home');
        return;
      }

      loadAllData();
    } else {
      navigate('/authentication/side-login');
    }
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCampanhas(),
        loadSorteios(),
        loadGanhadores(),
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
    
    // Carregar usuários APÓS terminar o loading geral (não bloqueante)
    loadUsuarios();
  };

  // ==================== CAMPANHAS ====================

  const loadCampanhas = async () => {
    try {
      const { data, error } = await supabase
        .from('campanhas_cupons')
        .select('*')
        .order('periodo_inicio', { ascending: true });

      if (error) throw error;

      setCampanhas(data || []);

      const ativa = data?.find((c) => c.status === 'ativa') || null;
      setCampanhaAtiva(ativa);
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
      toast.error('Erro ao carregar campanhas');
    }
  };

  const saveCampanha = async () => {
    // Validação mínima
    if (!formCampanha.nome || !formCampanha.periodo_inicio || !formCampanha.periodo_fim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      // Parse data com validação rigorosa
      let inicio: Date;
      let fim: Date;

      try {
        inicio = parseDateTimeLocal(formCampanha.periodo_inicio as string);
      } catch (error) {
        toast.error('Data de início inválida');
        console.error('Erro ao fazer parse da data de início:', error);
        return;
      }

      try {
        fim = parseDateTimeLocal(formCampanha.periodo_fim as string);
      } catch (error) {
        toast.error('Data de fim inválida');
        console.error('Erro ao fazer parse da data de fim:', error);
        return;
      }

      // Validar se as datas são válidas
      if (isNaN(inicio.getTime())) {
        toast.error('Data de início inválida');
        return;
      }

      if (isNaN(fim.getTime())) {
        toast.error('Data de fim inválida');
        return;
      }

      if (inicio >= fim) {
        toast.error('A data de início deve ser anterior à data de fim');
        return;
      }

      setLoadingCampanha(true);

      // mes_referencia deve ter o formato YYYY-MM (ex: 2025-11)
      const year = inicio.getFullYear();
      const month = String(inicio.getMonth() + 1).padStart(2, '0');
      const mesReferencia = `${year}-${month}`;

      // Se selecionou um arquivo de banner, fazer upload para o bucket 'xizinha'
      let bannerUrlToSave: string | null = formCampanha.banner_url || null;
      if (selectedBannerFile) {
        setUploadingBanner(true);
        try {
          const ext = selectedBannerFile.name.split('.').pop() || 'jpg';
          const filename = `campanhas/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

          const { error: uploadError } = await supabase.storage
            .from('xizinha')
            .upload(filename, selectedBannerFile, { cacheControl: '3600', upsert: false });

          if (uploadError) throw uploadError;

          const { data: publicData } = supabase.storage.from('xizinha').getPublicUrl(filename);
          bannerUrlToSave = publicData?.publicUrl || null;
        } catch (err) {
          console.error('Erro ao fazer upload do banner:', err);
          toast.error('Erro ao enviar imagem do banner');
        } finally {
          setUploadingBanner(false);
        }
      }

      // Se selecionou um arquivo de regulamento, fazer upload para o bucket 'xizinha'
      let regulamentoUrlToSave: string | null = formCampanha.regulamento_url || null;
      if (selectedRegulamentoFile) {
        setUploadingRegulamento(true);
        try {
          const ext = selectedRegulamentoFile.name.split('.').pop() || 'pdf';
          const filename = `campanhas/regulamentos/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

          const { error: uploadError } = await supabase.storage
            .from('xizinha')
            .upload(filename, selectedRegulamentoFile, { cacheControl: '3600', upsert: false });

          if (uploadError) throw uploadError;

          const { data: publicData } = supabase.storage.from('xizinha').getPublicUrl(filename);
          regulamentoUrlToSave = publicData?.publicUrl || null;
        } catch (err) {
          console.error('Erro ao fazer upload do regulamento:', err);
          toast.error('Erro ao enviar arquivo do regulamento');
        } finally {
          setUploadingRegulamento(false);
        }
      }

      // Armazenar como string no formato YYYY-MM-DDTHH:mm:ss (sem timezone)
      const inicioString = formatToISOLocalString(inicio);
      const fimString = formatToISOLocalString(fim);

      if (editingCampanha) {
        const updatePayload: any = {
          nome: formCampanha.nome,
          descricao: formCampanha.descricao,
          periodo_inicio: inicioString,
          periodo_fim: fimString,
          status: formCampanha.status,
          updated_at: new Date().toISOString(),
        };

        // incluir banner_url e regulamento_url explicitamente (podem ser null para limpar)
        if (bannerUrlToSave !== undefined) updatePayload.banner_url = bannerUrlToSave;
        if (regulamentoUrlToSave !== undefined) updatePayload.regulamento_url = regulamentoUrlToSave;

        const { error } = await supabase
          .from('campanhas_cupons')
          .update(updatePayload)
          .eq('id', editingCampanha.id);

        if (error) throw error;
        toast.success('Campanha atualizada com sucesso');
      } else {
        const insertPayload: any = {
          nome: formCampanha.nome,
          descricao: formCampanha.descricao,
          periodo_inicio: inicioString,
          periodo_fim: fimString,
          status: formCampanha.status || 'ativa',
          mes_referencia: mesReferencia,
          ano: year,
        };
        if (bannerUrlToSave) insertPayload.banner_url = bannerUrlToSave;
        if (regulamentoUrlToSave) insertPayload.regulamento_url = regulamentoUrlToSave;

        const { error } = await supabase.from('campanhas_cupons').insert([insertPayload]);

        if (error) throw error;
        toast.success('Campanha criada com sucesso');
      }

      setFormCampanha({});
      setEditingCampanha(null);
      setDialogCampanha(false);
      setSelectedRegulamentoFile(null);
      setRegulamentoFileName(null);
      loadCampanhas();
    } catch (error) {
      console.error('Erro ao salvar campanha:', error);
      toast.error('Erro ao salvar campanha');
    } finally {
      setLoadingCampanha(false);
    }
  };

  const deleteCampanha = async (id: string) => {
    try {
      const { error } = await supabase
        .from('campanhas_cupons')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Campanha deletada');
      loadCampanhas();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar campanha');
    }
  };

  // ==================== SORTEIOS ====================

  const loadSorteios = async () => {
    try {
      const { data, error } = await supabase
        .from('sorteios_cupons')
        .select('*')
        .order('data_sorteio', { ascending: false });

      if (error) throw error;

      setSorteios(data || []);
    } catch (error) {
      console.error('Erro ao carregar sorteios:', error);
      toast.error('Erro ao carregar sorteios');
    }
  };

  const saveSorteio = async () => {
    if (!formSorteio.campanha_id || !formSorteio.titulo || !formSorteio.data_sorteio) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setLoadingSorteios(true);
    try {
      if (editingSorteio) {
        const { error } = await supabase
          .from('sorteios_cupons')
          .update({
            titulo: formSorteio.titulo,
            descricao: formSorteio.descricao,
            data_sorteio: formSorteio.data_sorteio,
            tipo: formSorteio.tipo,
            status: formSorteio.status,
            premio_descricao: formSorteio.premio_descricao,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingSorteio.id);

        if (error) throw error;
        toast.success('Sorteio atualizado');
      } else {
        const { error } = await supabase.from('sorteios_cupons').insert([
          {
            campanha_id: formSorteio.campanha_id,
            titulo: formSorteio.titulo,
            descricao: formSorteio.descricao,
            data_sorteio: formSorteio.data_sorteio,
            tipo: formSorteio.tipo || 'semanal',
            status: 'pendente',
            premio_descricao: formSorteio.premio_descricao,
            total_cupons_participantes: 0,
            total_participantes_unicos: 0,
          },
        ]);

        if (error) throw error;
        toast.success('Sorteio criado');
      }

      setFormSorteio({});
      setEditingSorteio(null);
      setDialogSorteio(false);
      loadSorteios();
    } catch (error) {
      console.error('Erro ao salvar sorteio:', error);
      toast.error('Erro ao salvar sorteio');
    } finally {
      setLoadingSorteios(false);
    }
  };

  const deleteSorteio = async (id: string) => {
    try {
      const { error } = await supabase
        .from('sorteios_cupons')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Sorteio deletado');
      loadSorteios();
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast.error('Erro ao deletar sorteio');
    }
  };

  // ==================== USUÁRIOS E CUPONS ====================

  const loadUsuarios = async () => {
    setLoadingUsuarios(true);
    try {
      const usuariosData = await couponsService.getUsuariosComCupons();
      setUsuarios(usuariosData);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      toast.error('Erro ao carregar usuários');
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const usuariosFiltrados = usuarios.filter(
    (u) =>
      u.cliente_nome.toLowerCase().includes(searchUsuario.toLowerCase()) ||
      String(u.cliente_codigo).includes(searchUsuario) ||
      u.cliente_email.toLowerCase().includes(searchUsuario.toLowerCase()) ||
      (u.cliente_telefone?.toLowerCase().includes(searchUsuario.toLowerCase()) ?? false)
  );

  // Helper: Buscar informações do sorteio
  const getSorteioBuscado = (sorteioId: string) => {
    return sorteios.find((s) => s.id === sorteioId);
  };

  // ==================== GANHADORES ====================

  const loadGanhadores = async () => {
    setLoadingGanhadores(true);
    try {
      const { data, error } = await supabase
        .from('ganhadores_sorteios')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setGanhadores(data || []);
    } catch (error) {
      console.error('Erro ao carregar ganhadores:', error);
      toast.error('Erro ao carregar ganhadores');
    } finally {
      setLoadingGanhadores(false);
    }
  };

  const updateGanhador = async (ganhadorId: string, updates: any) => {
    try {
      const { error } = await supabase
        .from('ganhadores_sorteios')
        .update(updates)
        .eq('id', ganhadorId);

      if (error) throw error;

      toast.success('Ganhador atualizado');
      loadGanhadores();
      setDialogGanhador(false);
    } catch (error) {
      console.error('Erro ao atualizar ganhador:', error);
      toast.error('Erro ao atualizar');
    }
  };

  // Atualizar status do sorteio (pendente | realizado | cancelado)
  const updateSorteioStatus = async (
    sorteioId: string,
    novoStatus: 'pendente' | 'realizado' | 'cancelado'
  ) => {
    try {
      const updateData: any = {
        status: novoStatus,
        updated_at: new Date().toISOString(),
      };

      if (novoStatus === 'realizado') {
        updateData.realizado_em = new Date().toISOString();
      }

      const { error } = await supabase
        .from('sorteios_cupons')
        .update(updateData)
        .eq('id', sorteioId);

      if (error) throw error;

      toast.success('Status do sorteio atualizado');
      loadSorteios();
    } catch (err) {
      console.error('Erro ao atualizar status do sorteio:', err);
      toast.error('Erro ao atualizar status do sorteio');
    }
  };

  // ==================== BUSCAR E ADICIONAR GANHADOR ====================

  const buscarCupom = async () => {
    if (!formGanhador.numero_cupom) {
      toast.error('Informe o número do cupom');
      return;
    }

    setLoadingBuscaCupom(true);
    try {
      const response = await httpInterceptor.get(`/coupons/winner/${formGanhador.numero_cupom}`);

      if (!response.ok) {
        if (response.status === 404) {
          toast.error('Cupom não encontrado');
          setDadosCupom(null);
        } else {
          throw new Error(`Erro ao buscar cupom: ${response.statusText}`);
        }
        return;
      }

      const resultado = await response.json();
      setDadosCupom(resultado.data);
      toast.success('Cupom encontrado!');
    } catch (error) {
      console.error('Erro ao buscar cupom:', error);
      toast.error('Erro ao buscar cupom');
      setDadosCupom(null);
    } finally {
      setLoadingBuscaCupom(false);
    }
  };

  const adicionarNovoGanhador = async () => {
    if (!formGanhador.numero_cupom || !formGanhador.sorteio_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setAdicionandoGanhador(true);
    try {
      const response = await httpInterceptor.post('/coupons/winner', {
        numero_cupom: formGanhador.numero_cupom,
        sorteio_id: formGanhador.sorteio_id,
        premio_descricao: formGanhador.premio_descricao || undefined,
        validado: formGanhador.validado || false,
        presente_na_live: formGanhador.presente_na_live || false,
        observacoes: formGanhador.observacoes || undefined,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erro ao adicionar ganhador: ${response.statusText}`);
      }

      toast.success('Ganhador adicionado com sucesso');
      setFormGanhador({});
      setDadosCupom(null);
      setDialogNovoGanhador(false);
      loadGanhadores();
    } catch (error) {
      console.error('Erro ao adicionar ganhador:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao adicionar ganhador');
    } finally {
      setAdicionandoGanhador(false);
    }
  };

  // ==================== DOWNLOAD CSV ====================

  const handleDownloadActiveCouponsCSV = async () => {
    setLoadingExport(true);
    try {
      const blob = await couponsService.exportActiveCouponsCSV();

      // Criar URL para o blob e disparar download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cupons_ativos_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('CSV exportado com sucesso');
    } catch (error) {
      console.error('Erro ao exportar cupons:', error);
      toast.error('Erro ao exportar cupons ativos');
    } finally {
      setLoadingExport(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="px-4 pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-2/3"></div>
            <div className="h-32 bg-muted rounded"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background pb-10">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-6 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <Gift className="w-8 h-8" />
                Gerenciamento de Cupons & Sorteios
              </h1>
              <p className="text-sm opacity-90 mt-1">
                Gerencie campanhas, sorteios e cupons dos clientes
              </p>
            </div>
            <Button
              onClick={handleDownloadActiveCouponsCSV}
              disabled={loadingExport}
              className="bg-white text-purple-600 hover:bg-gray-100 font-semibold flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {loadingExport ? 'Exportando...' : 'Exportar Cupons Ativos'}
            </Button>
          </div>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="campanhas" className="flex items-center gap-1">
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Campanhas</span>
            </TabsTrigger>
            <TabsTrigger value="sorteios" className="flex items-center gap-1">
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Sorteios</span>
            </TabsTrigger>
            <TabsTrigger value="usuarios" className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Usuários</span>
            </TabsTrigger>
            <TabsTrigger value="ganhadores" className="flex items-center gap-1">
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Resultados</span>
            </TabsTrigger>
          </TabsList>

          {/* TAB: CAMPANHAS */}
          <TabsContent value="campanhas" className="space-y-6 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Campanhas de Cupons</h2>
              <div className="flex gap-2 items-center">
                <Button
                  variant={filterAtivas ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterAtivas(!filterAtivas)}
                  className={filterAtivas ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  {filterAtivas ? "Todas" : "Apenas Ativas"}
                </Button>
                <Dialog open={dialogCampanha} onOpenChange={setDialogCampanha}>
                  <DialogTrigger asChild>
                  <Button
                    onClick={() => {
                      // Gerar defaults para nova campanha
                      const hoje = new Date();
                      const inicioDefault = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 0, 0, 0);
                      const fimDefault = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59);
                      
                      const inicioString = formatToISOLocalString(inicioDefault);
                      const fimString = formatToISOLocalString(fimDefault);
                      
                      setEditingCampanha(null);
                      setFormCampanha({
                        periodo_inicio: inicioString,
                        periodo_fim: fimString,
                      });
                      setSelectedBannerFile(null);
                      setBannerPreview(null);
                      setSelectedRegulamentoFile(null);
                      setRegulamentoFileName(null);
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nova Campanha
                  </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingCampanha ? 'Editar Campanha' : 'Nova Campanha'}
                      </DialogTitle>
                    </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="nome">Nome *</Label>
                      <Input
                        id="nome"
                        placeholder="Ex: Campanha Março 2025"
                        value={formCampanha.nome || ''}
                        onChange={(e) =>
                          setFormCampanha({ ...formCampanha, nome: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="descricao">Descrição</Label>
                      <Textarea
                        id="descricao"
                        placeholder="Descreva a campanha..."
                        value={formCampanha.descricao || ''}
                        onChange={(e) =>
                          setFormCampanha({
                            ...formCampanha,
                            descricao: e.target.value,
                          })
                        }
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="inicio">Data de Início *</Label>
                      <Input
                        id="inicio"
                        type="datetime-local"
                        value={formCampanha.periodo_inicio || ''}
                        onChange={(e) =>
                          setFormCampanha({
                            ...formCampanha,
                            periodo_inicio: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="fim">Data de Fim *</Label>
                      <Input
                        id="fim"
                        type="datetime-local"
                        value={formCampanha.periodo_fim || ''}
                        onChange={(e) =>
                          setFormCampanha({
                            ...formCampanha,
                            periodo_fim: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formCampanha.status || 'ativa'}
                        onValueChange={(value: any) =>
                          setFormCampanha({ ...formCampanha, status: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ativa">Ativa</SelectItem>
                          <SelectItem value="pausada">Pausada</SelectItem>
                          <SelectItem value="encerrada">Encerrada</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Upload de banner */}
                    <div>
                      <Label>Banner (opcional)</Label>
                      <div className="flex flex-col gap-2">
                        {bannerPreview ? (
                          <div className="relative">
                            <img
                              src={bannerPreview}
                              alt="Pré-visualização do banner"
                              className="w-full h-40 object-cover rounded"
                            />
                            <div className="absolute top-2 right-2 flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedBannerFile(null);
                                  setBannerPreview(null);
                                  // se estava editando e havia banner salvo, também limpar do form para salvar nulo
                                  setFormCampanha({ ...formCampanha, banner_url: null });
                                }}
                              >
                                Remover
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              id="banner"
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                if (file) {
                                  setSelectedBannerFile(file);
                                  setBannerPreview(URL.createObjectURL(file));
                                }
                              }}
                            />
                          </div>
                        )}
                        {formCampanha.banner_url && !bannerPreview && (
                          <div className="text-xs text-muted-foreground">Imagem salva: <span className="font-medium break-words">{formCampanha.banner_url}</span></div>
                        )}
                      </div>
                    </div>

                    {/* Upload de regulamento */}
                    <div>
                      <Label>Regulamento (opcional)</Label>
                      <div className="flex flex-col gap-2">
                        {regulamentoFileName ? (
                          <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-900 truncate">
                                {regulamentoFileName}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedRegulamentoFile(null);
                                setRegulamentoFileName(null);
                                setFormCampanha({ ...formCampanha, regulamento_url: null });
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              id="regulamento"
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                if (file) {
                                  if (!file.type.includes('pdf') && !file.type.includes('word') && !file.type.includes('document')) {
                                    toast.error('Apenas arquivos PDF ou DOC são permitidos');
                                    return;
                                  }
                                  setSelectedRegulamentoFile(file);
                                  setRegulamentoFileName(file.name);
                                }
                              }}
                              className="text-sm"
                            />
                          </div>
                        )}
                        {formCampanha.regulamento_url && !regulamentoFileName && (
                          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-2">
                            <FileText className="w-4 h-4 text-green-600" />
                            <a
                              href={formCampanha.regulamento_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-green-700 hover:underline flex-1 truncate"
                            >
                              Regulamento salvo
                            </a>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setFormCampanha({ ...formCampanha, regulamento_url: null });
                              }}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={saveCampanha}
                        disabled={loadingCampanha || uploadingRegulamento}
                        className="flex-1"
                      >
                        {loadingCampanha ? 'Salvando...' : 'Salvar'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setDialogCampanha(false)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            <div className="grid gap-4">
              {(() => {
                const campanhasFiltradas = filterAtivas
                  ? campanhas.filter((c) => c.status === 'ativa')
                  : campanhas;
                const campanhasOrdenadas = [...campanhasFiltradas].sort((a, b) => {
                  const dataA = new Date(a.periodo_inicio).getTime();
                  const dataB = new Date(b.periodo_inicio).getTime();
                  return dataB - dataA; // Decrescente
                });
                return campanhasOrdenadas.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                      <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">
                        {filterAtivas ? 'Nenhuma campanha ativa' : 'Nenhuma campanha criada'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  campanhasOrdenadas.map((campanha) => (
                  <Card
                    key={campanha.id}
                    className={
                      campanha.id === campanhaAtiva?.id
                        ? 'border-primary bg-primary/5'
                        : ''
                    }
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {campanha.nome}
                            {campanha.status === 'ativa' && (
                              <Badge className="bg-green-600">Ativa</Badge>
                            )}
                            {campanha.status === 'pausada' && (
                              <Badge variant="secondary">Pausada</Badge>
                            )}
                            {campanha.status === 'encerrada' && (
                              <Badge variant="destructive">Encerrada</Badge>
                            )}
                          </CardTitle>
                          <CardDescription className="mt-1">
                            {campanha.descricao}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingCampanha(campanha);
                              setFormCampanha(campanha);
                              setSelectedBannerFile(null);
                              setBannerPreview(campanha.banner_url || null);
                              setSelectedRegulamentoFile(null);
                              setRegulamentoFileName(null);
                              setDialogCampanha(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogContent>
                              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja deletar esta campanha?
                              </AlertDialogDescription>
                              <div className="flex gap-2 justify-end">
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deleteCampanha(campanha.id)}
                                >
                                  Deletar
                                </AlertDialogAction>
                              </div>
                            </AlertDialogContent>
                            {/* <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </DialogTrigger> */}
                          </AlertDialog>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Início</p>
                          <p className="font-semibold">
                            {(() => {
                              try {
                                const dataInicio = parseDateTimeLocal(campanha.periodo_inicio);
                                return format(dataInicio, "dd/MM/yyyy HH:mm", { locale: ptBR });
                              } catch {
                                return 'Data inválida';
                              }
                            })()}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Fim</p>
                          <p className="font-semibold">
                            {(() => {
                              try {
                                const dataFim = parseDateTimeLocal(campanha.periodo_fim);
                                return format(dataFim, "dd/MM/yyyy HH:mm", { locale: ptBR });
                              } catch {
                                return 'Data inválida';
                              }
                            })()}
                          </p>
                        </div>
                        {campanha.banner_url && (
                          <div className="mb-4">
                            <p className="text-muted-foreground">Banner</p>
                            <img
                              src={campanha.banner_url}
                              alt={campanha.nome}
                              className="w-full h-44 object-cover rounded-lg shadow-sm"
                            />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  ))
                );
              })()}
            </div>
          </TabsContent>

          {/* TAB: SORTEIOS */}
          <TabsContent value="sorteios" className="space-y-6 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Sorteios</h2>
              <Dialog open={dialogSorteio} onOpenChange={setDialogSorteio}>
                <DialogTrigger asChild>
                  <Button
                    onClick={() => {
                      setEditingSorteio(null);
                      setFormSorteio({});
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Sorteio
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingSorteio ? 'Editar Sorteio' : 'Novo Sorteio'}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="campanha">Campanha *</Label>
                      <Select
                        value={formSorteio.campanha_id || ''}
                        onValueChange={(value) =>
                          setFormSorteio({ ...formSorteio, campanha_id: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione uma campanha" />
                        </SelectTrigger>
                        <SelectContent>
                          {campanhas.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="titulo">Título *</Label>
                      <Input
                        id="titulo"
                        placeholder="Ex: Sorteio Semanal"
                        value={formSorteio.titulo || ''}
                        onChange={(e) =>
                          setFormSorteio({ ...formSorteio, titulo: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="tipo">Tipo</Label>
                      <Select
                        value={formSorteio.tipo || 'semanal'}
                        onValueChange={(value: any) =>
                          setFormSorteio({ ...formSorteio, tipo: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="semanal">Semanal</SelectItem>
                          <SelectItem value="super_live">Super Live</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="data">Data do Sorteio *</Label>
                      <Input
                        id="data"
                        type="datetime-local"
                        value={formSorteio.data_sorteio || ''}
                        onChange={(e) =>
                          setFormSorteio({
                            ...formSorteio,
                            data_sorteio: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="premio">Descrição do Prêmio</Label>
                      <Textarea
                        id="premio"
                        placeholder="Ex: Pulseira de ouro 18k&#10;Material: Ouro&#10;Tamanho: Ajustável"
                        value={formSorteio.premio_descricao || ''}
                        onChange={(e) =>
                          setFormSorteio({
                            ...formSorteio,
                            premio_descricao: e.target.value,
                          })
                        }
                        rows={4}
                        className="resize-none"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Você pode usar quebras de linha para descrever melhor o prêmio</p>
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button
                        onClick={saveSorteio}
                        disabled={loadingSorteios}
                        className="flex-1"
                      >
                        {loadingSorteios ? 'Salvando...' : 'Salvar'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setDialogSorteio(false)}
                        className="flex-1"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {sorteios.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Nenhum sorteio criado</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {sorteios.map((sorteio) => {
                    const campanha = campanhas.find((c) => c.id === sorteio.campanha_id);
                    return (
                      <Card key={sorteio.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="flex items-center gap-2">
                                {sorteio.titulo}
                                <Badge
                                  variant={
                                    sorteio.status === 'pendente' ? 'secondary' : (sorteio.status === 'realizado' ? 'default' : 'outline')
                                  }
                                >
                                  {sorteio.status === 'pendente' && (
                                    <>
                                      <Clock className="w-3 h-3 mr-1" />
                                      Pendente
                                    </>
                                  )}
                                  {sorteio.status === 'realizado' && (
                                    <>
                                      <CheckCircle2 className="w-3 h-3 mr-1" />
                                      Realizado
                                    </>
                                  )}
                                  {sorteio.status === 'cancelado' && (
                                    <>
                                      <AlertCircle className="w-3 h-3 mr-1" />
                                      Cancelado
                                    </>
                                  )}
                                </Badge>
                              </CardTitle>
                              <CardDescription className="mt-1">
                                Campanha: {campanha?.nome || 'Desconhecida'}
                              </CardDescription>
                            </div>
                            <div className="flex gap-2 items-center">
                              <div className="w-40">
                                <Select
                                  value={sorteio.status || 'pendente'}
                                  onValueChange={(value) =>
                                    updateSorteioStatus(sorteio.id, value as any)
                                  }
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pendente">Pendente</SelectItem>
                                    <SelectItem value="realizado">Realizado</SelectItem>
                                    <SelectItem value="cancelado">Cancelado</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingSorteio(sorteio);
                                  setFormSorteio(sorteio);
                                  setDialogSorteio(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogContent>
                                  <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja deletar este sorteio?
                                  </AlertDialogDescription>
                                  <div className="flex gap-2 justify-end">
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteSorteio(sorteio.id)}
                                    >
                                      Deletar
                                    </AlertDialogAction>
                                  </div>
                                </AlertDialogContent>
                                {/* <DialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </DialogTrigger> */}
                              </AlertDialog>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-muted-foreground">Data do Sorteio</p>
                              <p className="font-semibold">
                                {format(new Date(sorteio.data_sorteio), "dd/MM/yyyy HH:mm", {
                                  locale: ptBR,
                                })}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Tipo</p>
                              <p className="font-semibold">
                                {sorteio.tipo === 'semanal' ? 'Semanal' : 'Super Live'}
                              </p>
                            </div>
                          </div>
                          {sorteio.premio_descricao && (
                            <div>
                              <p className="text-muted-foreground">Prêmio</p>
                              <p className="font-semibold whitespace-pre-wrap">{sorteio.premio_descricao}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB: USUÁRIOS */}
          <TabsContent value="usuarios" className="space-y-6 mt-6">
            <div className="space-y-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nome, código ou email..."
                    className="pl-10"
                    value={searchUsuario}
                    onChange={(e) => setSearchUsuario(e.target.value)}
                  />
                </div>
              </div>

              {loadingUsuarios ? (
                <Card>
                  <CardContent className="p-8">
                    <div className="animate-pulse space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                    </div>
                  </CardContent>
                </Card>
              ) : usuariosFiltrados.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Nenhum usuário com cupons</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="rounded-lg border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-center">Total</TableHead>
                        <TableHead className="text-center">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usuariosFiltrados.map((usuario) => (
                        <TableRow key={usuario.cliente_id}>
                          <TableCell>
                            <div>
                              <p className="font-semibold">
                                <span className='text-muted-foreground text-xs mr-1'>#{usuario.cliente_codigo}</span>{usuario.cliente_nome}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {usuario.cliente_telefone}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell className="text-center font-semibold">
                            {usuario.total_cupons}
                          </TableCell>
                          <TableCell className="text-center">
                            <Dialog open={dialogDetalhes} onOpenChange={setDialogDetalhes}>
                              <DialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={
                                    async () => {
                                      try {
                                        usuario.cupons = await couponsService.getClientCoupons(usuario.cliente_id);
                                        setSelectedUsuario(usuario)
                                      } catch (error) {
                                        console.error('Erro ao buscar cupons:', error);
                                        toast.error('Erro ao carregar cupons do cliente');
                                      }
                                    }
                                  }
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              {selectedUsuario?.cliente_id === usuario.cliente_id && (
                                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle>Detalhes de Cupons</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-6">
                                    {/* Info do Cliente */}
                                    <Card>
                                      <CardHeader className="pb-3">
                                        <CardTitle className="text-lg">
                                          Informações Pessoais
                                        </CardTitle>
                                      </CardHeader>
                                      <CardContent className="space-y-3">
                                        <div className="grid grid-cols-2 gap-4">
                                          <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-muted-foreground" />
                                            <div>
                                              <p className="text-xs text-muted-foreground">
                                                Cliente
                                              </p>
                                              <p className="font-semibold">
                                                <span className='text-muted-foreground text-xs mr-1'>#{selectedUsuario.cliente_codigo}</span><br />
                                                {selectedUsuario.cliente_nome}
                                              </p>
                                            </div>
                                          </div>
                                          {selectedUsuario.cliente_instagram && (
                                            <div className="flex items-center gap-2 col-span-2">
                                              <Instagram className="w-4 h-4 text-muted-foreground" />
                                              <div>
                                                <p className="text-xs text-muted-foreground">
                                                  Instagram
                                                </p>
                                                <p className="font-semibold">
                                                  @{selectedUsuario.cliente_instagram}
                                                </p>
                                              </div>
                                            </div>
                                          )}
                                          {selectedUsuario.cliente_cidade && (
                                            <div className="flex items-center gap-2 col-span-2">
                                              <MapPin className="w-4 h-4 text-muted-foreground" />
                                              <div>
                                                <p className="text-xs text-muted-foreground">
                                                  Localização
                                                </p>
                                                <p className="font-semibold">
                                                  {selectedUsuario.cliente_cidade},{' '}
                                                  {selectedUsuario.cliente_estado}
                                                </p>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </CardContent>
                                    </Card>

                                    {/* Estatísticas de Cupons */}
                                    <Card>
                                      <CardHeader className="pb-3">
                                        <CardTitle className="text-lg">
                                          Estatísticas de Cupons
                                        </CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <div className="grid grid-cols-4 gap-4">
                                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                            <div className="text-2xl font-bold text-green-700">
                                              {selectedUsuario.cupons?.reduce((sum, c) => sum + (c.status === 'ativo' ? c.quantidade_cupons : 0), 0) || 0}
                                            </div>
                                            <p className="text-xs text-green-600">Ativos</p>
                                          </div>
                                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                                            <div className="text-2xl font-bold text-yellow-700">
                                              {selectedUsuario.cupons?.reduce((sum, c) => sum + (c.status === 'inativo' ? c.quantidade_cupons : 0), 0) || 0}
                                            </div>
                                            <p className="text-xs text-yellow-600">Inativos</p>
                                          </div>
                                          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                                            <div className="text-2xl font-bold text-red-700">
                                              {selectedUsuario.cupons?.reduce((sum, c) => sum + (c.status === 'cancelado' ? c.quantidade_cupons : 0), 0) || 0}
                                            </div>
                                            <p className="text-xs text-red-600">Cancelados</p>
                                          </div>
                                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                                            <div className="text-2xl font-bold text-purple-700">
                                              {selectedUsuario.cupons?.reduce((sum, c) => sum + c.quantidade_cupons, 0) || 0}
                                            </div>
                                            <p className="text-xs text-purple-600">Total</p>
                                          </div>
                                        </div>
                                      </CardContent>
                                    </Card>

                                    {/* Lista de Cupons */}
                                    <Card>
                                      <CardHeader className="pb-3">
                                        <CardTitle className="text-lg">
                                          Cupons do Cliente
                                        </CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <div className="space-y-3 max-h-96 overflow-y-auto">
                                          {selectedUsuario.cupons.sort(
                                            (a, b) => new Date(b.data_geracao).getTime() - new Date(a.data_geracao).getTime()
                                          ).map((cupom, idx) => (
                                            <div
                                              key={idx}
                                              className="border rounded-lg p-3 space-y-2"
                                            >
                                              <div className="flex items-start justify-between">
                                                <div>
                                                  <p className="font-semibold text-sm">
                                                    {cupom.numero_cupom}
                                                  </p>
                                                  <p className="text-xs text-muted-foreground">
                                                    {cupom.quantidade_cupons} cupom(ns)
                                                  </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <Select
                                                    value={cupom.status}
                                                    onValueChange={async (novoStatus) => {
                                                      try {
                                                        await couponsService.updateCupomStatus(
                                                          cupom.id,
                                                          novoStatus as 'ativo' | 'inativo' | 'cancelado'
                                                        );
                                                        toast.success('Status do cupom atualizado');
                                                        // Recarregar cupons do cliente
                                                        if (selectedUsuario) {
                                                          selectedUsuario.cupons = await couponsService.getClientCoupons(selectedUsuario.cliente_id);
                                                          setSelectedUsuario({ ...selectedUsuario });
                                                        }
                                                      } catch (error) {
                                                        console.error('Erro ao atualizar status:', error);
                                                        toast.error('Erro ao atualizar status do cupom');
                                                      }
                                                    }}
                                                  >
                                                    <SelectTrigger className={`w-28 h-8
                                                        ${cupom.status === 'ativo' ? 'bg-green-500/30' : ''}
                                                        ${cupom.status === 'inativo' ? 'bg-yellow-500/30' : ''}
                                                        ${cupom.status === 'cancelado' ? 'bg-red-500/30' : ''}
                                                      `}>
                                                      <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="ativo">
                                                        <span className="flex items-center gap-2">
                                                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                                          Ativo
                                                        </span>
                                                      </SelectItem>
                                                      <SelectItem value="inativo">
                                                        <span className="flex items-center gap-2">
                                                          <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                                                          Inativo
                                                        </span>
                                                      </SelectItem>
                                                      <SelectItem value="cancelado">
                                                        <span className="flex items-center gap-2">
                                                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                                          Cancelado
                                                        </span>
                                                      </SelectItem>
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                              </div>
                                              <div className="grid grid-cols-2 gap-2 text-xs">
                                                <div>
                                                  <p className="text-muted-foreground">
                                                    Origem
                                                  </p>
                                                  <p className="font-semibold">
                                                    {cupom.origem === 'compra'
                                                      ? 'Compra'
                                                      : 'Indicação'}
                                                  </p>
                                                </div>
                                                <div>
                                                  <p className="text-muted-foreground">
                                                    Gerado em
                                                  </p>
                                                  <p className="font-semibold">
                                                    {format(new Date(cupom.data_geracao), 'dd/MM')}
                                                  </p>
                                                </div>
                                              </div>
                                              {cupom.valor_compra && (
                                                <div className="text-xs">
                                                  <p className="text-muted-foreground">
                                                    Valor da Compra
                                                  </p>
                                                  <p className="font-semibold">
                                                    R$ {cupom.valor_compra.toFixed(2)}
                                                  </p>
                                                </div>
                                              )}
                                              {cupom.status === 'cancelado' && (
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                                                  <div className="space-y-1">
                                                    <Label htmlFor={`motivo-${idx}`} className="text-red-700 font-semibold text-xs">
                                                      Motivo
                                                    </Label>
                                                    <div className="flex gap-2 items-end">
                                                      <Textarea
                                                        id={`motivo-${idx}`}
                                                        placeholder="Motivo do cancelamento..."
                                                        value={cupom.motivo_cancelamento || ''}
                                                        onChange={(e) => {
                                                          const novoMotivo = e.target.value;
                                                          selectedUsuario.cupons[idx].motivo_cancelamento = novoMotivo;
                                                          setSelectedUsuario({ ...selectedUsuario });
                                                        }}
                                                        rows={1}
                                                        className="text-xs flex-1 resize-none"
                                                      />
                                                      <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 px-2 bg-red-600 text-white hover:bg-red-700 border-red-600 flex-shrink-0"
                                                        onClick={async () => {
                                                          try {
                                                            await couponsService.updateCupomStatus(
                                                              cupom.id,
                                                              'cancelado',
                                                              cupom.motivo_cancelamento
                                                            );
                                                            toast.success('Motivo do cancelamento atualizado');
                                                          } catch (error) {
                                                            console.error('Erro ao atualizar motivo:', error);
                                                            toast.error('Erro ao salvar motivo do cancelamento');
                                                          }
                                                        }}
                                                      >
                                                        Salvar
                                                      </Button>
                                                    </div>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      </CardContent>
                                    </Card>
                                  </div>
                                </DialogContent>
                              )}
                            </Dialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB: GANHADORES */}
          <TabsContent value="ganhadores" className="space-y-6 mt-6">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Resultados de Sorteios</h2>
                <Dialog open={dialogNovoGanhador} onOpenChange={setDialogNovoGanhador}>
                  <DialogTrigger asChild>
                    <Button
                      onClick={() => {
                        setFormGanhador({});
                        setDadosCupom(null);
                      }}
                      className="bg-gradient-to-r from-purple-600 to-pink-600"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Novo Ganhador
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Adicionar Novo Ganhador</DialogTitle>
                      <DialogDescription>
                        Busque o cupom para registrar o ganhador
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Seção de Busca do Cupom */}
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm">Buscar Cupom</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div>
                            <Label htmlFor="numero_cupom">Número do Cupom *</Label>
                            <Input
                              id="numero_cupom"
                              placeholder="Ex: ABC-123-A"
                              value={formGanhador.numero_cupom || ''}
                              onChange={(e) =>
                                setFormGanhador({
                                  ...formGanhador,
                                  numero_cupom: e.target.value,
                                })
                              }
                            />
                          </div>
                          <Button
                            onClick={buscarCupom}
                            disabled={loadingBuscaCupom || !formGanhador.numero_cupom}
                            className="w-full"
                            variant="outline"
                          >
                            {loadingBuscaCupom ? 'Buscando...' : 'Buscar Cupom'}
                          </Button>
                        </CardContent>
                      </Card>

                      {/* Dados do Cupom Encontrado */}
                      {dadosCupom && (
                        <Card className="bg-blue-50 border-blue-200">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-sm text-blue-900">
                              ✓ Cupom Encontrado
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-2 text-sm">
                            <div>
                              <p className="text-muted-foreground text-xs">Cliente</p>
                              <p className="font-semibold">{dadosCupom.cliente_nome}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Código do Cliente</p>
                              <p className="font-semibold">#{dadosCupom.cliente_codigo}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Localização</p>
                              <p className="font-semibold">
                                {dadosCupom.cliente_cidade}, {dadosCupom.cliente_estado}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Instagram</p>
                              <p className="font-semibold">
                                {dadosCupom.cliente_instagram || 'Não informado'}
                              </p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Revendedora</p>
                              <p className="font-semibold">{dadosCupom.revendedora_nome}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground text-xs">Data de Validade</p>
                              <p className="font-semibold">
                                {format(new Date(dadosCupom.data_validade), 'dd/MM/yyyy', {
                                  locale: ptBR,
                                })}
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Seleção do Sorteio */}
                      {dadosCupom && (
                        <>
                          <div>
                            <Label htmlFor="sorteio_id">Sorteio *</Label>
                            <Select
                              value={formGanhador.sorteio_id || ''}
                              onValueChange={(value) =>
                                setFormGanhador({ ...formGanhador, sorteio_id: value })
                              }
                            >
                              <SelectTrigger id="sorteio_id">
                                <SelectValue placeholder="Selecione um sorteio" />
                              </SelectTrigger>
                              <SelectContent>
                                {sorteios.filter((sorteio) => sorteio.status === 'pendente').map((sorteio) => (
                                  <SelectItem key={sorteio.id} value={sorteio.id}>
                                    {sorteio.titulo}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Campos Adicionais */}
                          <div>
                            <Label htmlFor="premio_descricao">Descrição do Prêmio</Label>
                            <Textarea
                              id="premio_descricao"
                              placeholder="Descrição opcional do prêmio"
                              value={formGanhador.premio_descricao || ''}
                              onChange={(e) =>
                                setFormGanhador({
                                  ...formGanhador,
                                  premio_descricao: e.target.value,
                                })
                              }
                              rows={2}
                            />
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="validado"
                                checked={formGanhador.validado || false}
                                onCheckedChange={(checked) =>
                                  setFormGanhador({
                                    ...formGanhador,
                                    validado: checked as boolean,
                                  })
                                }
                              />
                              <Label htmlFor="validado" className="cursor-pointer">
                                Validado
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id="presente_na_live"
                                checked={formGanhador.presente_na_live || false}
                                onCheckedChange={(checked) =>
                                  setFormGanhador({
                                    ...formGanhador,
                                    presente_na_live: checked as boolean,
                                  })
                                }
                              />
                              <Label htmlFor="presente_na_live" className="cursor-pointer">
                                Presente na Live
                              </Label>
                            </div>
                          </div>

                          <div>
                            <Label htmlFor="observacoes">Observações</Label>
                            <Textarea
                              id="observacoes"
                              placeholder="Observações adicionais (opcional)"
                              value={formGanhador.observacoes || ''}
                              onChange={(e) =>
                                setFormGanhador({
                                  ...formGanhador,
                                  observacoes: e.target.value,
                                })
                              }
                              rows={2}
                            />
                          </div>
                        </>
                      )}

                      {/* Botões de Ação */}
                      <div className="flex gap-2 pt-4">
                        <Button
                          onClick={() => {
                            setFormGanhador({});
                            setDadosCupom(null);
                            setDialogNovoGanhador(false);
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={adicionarNovoGanhador}
                          disabled={!dadosCupom || !formGanhador.sorteio_id || adicionandoGanhador}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          {adicionandoGanhador ? 'Adicionando...' : 'Adicionar'}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {loadingGanhadores ? (
                <Card>
                  <CardContent className="p-8">
                    <div className="animate-pulse space-y-2">
                      <div className="h-4 bg-muted rounded w-3/4"></div>
                      <div className="h-4 bg-muted rounded w-1/2"></div>
                    </div>
                  </CardContent>
                </Card>
              ) : ganhadores.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">Nenhum ganhador registrado</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {ganhadores.map((ganhador) => (
                    <Card key={ganhador.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2 flex-1">
                            <div>
                              <h3 className="font-semibold flex items-center gap-2">
                                <span className="text-muted-foreground text-sm">#{ganhador.cliente_codigo}</span> {ganhador.cliente_nome}
                                {ganhador.validado && (
                                  <Badge className="bg-green-600">Validado</Badge>
                                )}
                                {!ganhador.validado && (
                                  <Badge variant="secondary">Pendente</Badge>
                                )}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                Sorteio: {getSorteioBuscado(ganhador.sorteio_id)?.titulo || 'Desconhecido'}
                              </p>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground text-xs">Prêmio</p>
                                <p className="font-semibold">{ganhador.premio_descricao}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Revendedora</p>
                                <p className="font-semibold">#{ganhador.revendedora_codigo} - {ganhador.revendedora_nome}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Instagram</p>
                                <p className="font-semibold">
                                  {ganhador.cliente_instagram
                                    ? `@${ganhador.cliente_instagram}`
                                    : '-'}
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground text-xs">Data</p>
                                <p className="font-semibold">
                                  {format(
                                    new Date(getSorteioBuscado(ganhador.sorteio_id)?.data_sorteio || ''),
                                    'dd/MM/yyyy'
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                          <Dialog open={dialogGanhador} onOpenChange={setDialogGanhador}>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedGanhador(ganhador)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            {selectedGanhador?.id === ganhador.id && (
                              <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Detalhes do Ganhador</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <Card>
                                    <CardContent className="pt-6 space-y-3">
                                      <div>
                                        <Label className="text-muted-foreground text-xs">
                                          Nome
                                        </Label>
                                        <p className="font-semibold">
                                          {selectedGanhador.cliente_nome}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-muted-foreground text-xs">
                                          Código
                                        </Label>
                                        <p className="font-semibold break-all">
                                          {selectedGanhador.cliente_codigo || '-'}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-muted-foreground text-xs">
                                          Localização
                                        </Label>
                                        <p className="font-semibold">
                                          {selectedGanhador.cliente_cidade},{' '}
                                          {selectedGanhador.cliente_estado}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-muted-foreground text-xs">
                                          Instagram
                                        </Label>
                                        <p className="font-semibold">
                                          {selectedGanhador.cliente_instagram
                                            ? `@${selectedGanhador.cliente_instagram}`
                                            : 'Não informado'}
                                        </p>
                                      </div>
                                      <div className="border-t pt-3">
                                        <Label className="text-muted-foreground text-xs">
                                          Sorteio
                                        </Label>
                                        <p className="font-semibold">
                                          {getSorteioBuscado(selectedGanhador.sorteio_id)?.titulo || 'Desconhecido'}
                                        </p>
                                      </div>
                                      <div>
                                        <Label className="text-muted-foreground text-xs">
                                          Prêmio
                                        </Label>
                                        <p className="font-semibold">
                                          {selectedGanhador.premio_descricao || 'Não especificado'}
                                        </p>
                                      </div>
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <Checkbox
                                            id="validado"
                                            checked={selectedGanhador.validado}
                                            onCheckedChange={(checked) => {
                                              updateGanhador(selectedGanhador.id, {
                                                validado: checked,
                                                data_validacao: checked
                                                  ? new Date().toISOString()
                                                  : null,
                                              });
                                            }}
                                          />
                                          <Label htmlFor="validado" className="cursor-pointer">
                                            Validado
                                          </Label>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Checkbox
                                            id="presente"
                                            checked={selectedGanhador.presente_na_live}
                                            onCheckedChange={(checked) => {
                                              updateGanhador(selectedGanhador.id, {
                                                presente_na_live: checked,
                                              });
                                            }}
                                          />
                                          <Label htmlFor="presente" className="cursor-pointer">
                                            Presente na Live
                                          </Label>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                </div>
                              </DialogContent>
                            )}
                          </Dialog>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminCupons;
